import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, ArrowLeftRight,
  PiggyBank, Banknote, DollarSign, Zap,
} from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { MoneyTransaction, MoneyTxType } from '../types';
import { Modal } from './ui/Modal';
import { formatVND } from '../utils/format';
import { v4 } from '../utils/uuid';
import { GLOBAL_WALLET_ID, CASH_WALLET_ASSET_ID } from '../utils/calculations';

// ── Helpers ───────────────────────────────────────────────────────────────────

function today() { return new Date().toISOString().split('T')[0]; }

function parseAmount(raw: string): number {
  const s = raw.replace(/[,\s]/g, '').toLowerCase();
  const m = s.match(/^(\d+(?:\.\d+)?)(tỷ|ty|triệu|tr|trieu|nghìn|ngàn|k)?$/);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  const u = m[2] ?? '';
  if (u.startsWith('tỷ') || u === 'ty')                                 return Math.round(n * 1_000_000_000);
  if (u.startsWith('tr') || u === 'triệu' || u === 'trieu')             return Math.round(n * 1_000_000);
  if (u === 'nghìn' || u === 'ngàn' || u === 'k')                       return Math.round(n * 1_000);
  if (n > 0 && n < 100_000)                                              return Math.round(n * 1_000_000);
  return Math.round(n);
}

// ── Type config ───────────────────────────────────────────────────────────────

const TYPE_CFG: Record<MoneyTxType, {
  label: string; emoji: string; desc: string; color: string; bg: string;
  Icon: React.FC<{ size?: number }>;
}> = {
  DEPOSIT:  { label: 'Nạp vốn',     emoji: '💰', desc: 'Lương, thưởng, tiền vào',    color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',   Icon: PiggyBank     },
  WITHDRAW: { label: 'Chi / Rút',    emoji: '💸', desc: 'Chi tiêu, rút tiền ra ngoài', color: '#f87171', bg: 'rgba(248,113,113,0.12)', Icon: Banknote      },
  BUY:      { label: 'Mua vào',      emoji: '📊', desc: 'Mua tài sản, đầu tư mới',    color: '#38bdf8', bg: 'rgba(56,189,248,0.12)',   Icon: TrendingUp    },
  SELL:     { label: 'Bán ra',       emoji: '📉', desc: 'Bán tài sản, thoát vị thế',  color: '#10b981', bg: 'rgba(16,185,129,0.12)',   Icon: TrendingDown  },
  INCOME:   { label: 'Thu nhập',     emoji: '🌱', desc: 'Cổ tức, lãi, thu hoạch',     color: '#a3e635', bg: 'rgba(163,230,53,0.12)',   Icon: DollarSign    },
  TRANSFER: { label: 'Chuyển quỹ',   emoji: '↔',  desc: 'Điều chuyển vốn giữa quỹ',   color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', Icon: ArrowLeftRight},
};

const QUICK_TYPES: MoneyTxType[] = ['DEPOSIT', 'WITHDRAW', 'BUY', 'SELL', 'INCOME', 'TRANSFER'];

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

export function QuickTransactionModal({ open, onClose }: Props) {
  const { state, dispatch } = useAppState();

  const [type,      setType]      = useState<MoneyTxType>('DEPOSIT');
  const [amountRaw, setAmountRaw] = useState('');
  const [note,      setNote]      = useState('');
  const [date,      setDate]      = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);

  const amount  = parseAmount(amountRaw);
  const isValid = amount > 0 && !!date;
  const cfg     = TYPE_CFG[type];

  // Số dư ví hiện tại
  const walletAsset = (state.assets ?? []).find(a => a.id === CASH_WALLET_ASSET_ID);
  const walletBal   = walletAsset?.currentPrice ?? 0;

  const reset = () => {
    setType('DEPOSIT');
    setAmountRaw('');
    setNote('');
    setDate(today());
    setSuccess(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = () => {
    if (!isValid || submitting) return;
    setSubmitting(true);

    // DEPOSIT/SELL/INCOME: tiền vào từ ngoài → ví
    // WITHDRAW/BUY: tiền ra từ ví → ngoài
    // TRANSFER: ví → ví (quick form dùng global wallet cho cả hai)
    const fromExternal = ['DEPOSIT', 'SELL', 'INCOME'].includes(type);
    const toExternal   = ['WITHDRAW', 'BUY'].includes(type);

    const tx: MoneyTransaction = {
      id:           v4(),
      type,
      net_amount:   amount,
      gross_amount: amount,
      fees:         0,
      date,
      reason:       note || cfg.label,
      note:         note || undefined,
      fund_id:      GLOBAL_WALLET_ID,
      from_node:    fromExternal ? { kind: 'external' } : { kind: 'wallet', fund_id: GLOBAL_WALLET_ID },
      to_node:      toExternal   ? { kind: 'external' } : { kind: 'wallet', fund_id: GLOBAL_WALLET_ID },
    };

    dispatch({ type: 'ADD_MONEY_TX', payload: tx });

    // Cập nhật số dư ví chung
    if (walletAsset) {
      const delta = ['DEPOSIT', 'SELL', 'INCOME'].includes(type) ? amount : -amount;
      dispatch({ type: 'UPDATE_ASSET', payload: {
        ...walletAsset, currentPrice: walletAsset.currentPrice + delta,
      }});
    }

    setSubmitting(false);
    setSuccess(true);

    // Tự đóng sau 1.2s
    setTimeout(() => { reset(); onClose(); }, 1200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid) handleSubmit();
    if (e.key === 'Escape') handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Giao dịch nhanh" subtitle="Nhập nhanh · chỉnh sửa chi tiết sau trong tab Giao dịch" size="sm">
      {success ? (
        /* ── Success state ── */
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--tx-1)', marginBottom: 4 }}>
            Đã ghi {cfg.label}
          </div>
          <div style={{ fontSize: 13, color: '#10b981', fontWeight: 700 }}>
            {formatVND(amount)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 6 }}>Đang đóng…</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} onKeyDown={handleKeyDown}>

          {/* ── Wallet balance hint ── */}
          {walletBal > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 14px', borderRadius: 10,
              background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.18)',
            }}>
              <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>Ví hiện tại:</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#10b981' }}>{formatVND(walletBal)}</span>
            </div>
          )}

          {/* ── Type selector ── */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, display: 'block' }}>
              Loại giao dịch
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 7 }}>
              {QUICK_TYPES.map(t => {
                const c   = TYPE_CFG[t];
                const sel = t === type;
                return (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    style={{
                      padding: '9px 6px', borderRadius: 10, cursor: 'pointer',
                      border: `1.5px solid ${sel ? c.color : 'var(--bd)'}`,
                      background: sel ? c.bg : 'var(--bg-card-el)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                      transition: 'all 0.12s',
                    }}
                    onMouseEnter={(e) => { if (!sel) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
                    onMouseLeave={(e) => { if (!sel) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-el)'; }}
                  >
                    <c.Icon size={14} />
                    <span style={{ fontSize: 10, fontWeight: sel ? 600 : 400, color: sel ? c.color : 'var(--tx-3)', whiteSpace: 'nowrap' }}>
                      {c.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Desc hint */}
            <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 6, paddingLeft: 2 }}>
              {cfg.emoji} {cfg.desc}
            </div>
          </div>

          {/* ── Amount ── */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, display: 'block' }}>
              Số tiền
            </label>
            <input
              autoFocus
              type="text"
              value={amountRaw}
              onChange={e => setAmountRaw(e.target.value)}
              placeholder="VD: 5tr · 1.5ty · 500k · 2000000"
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 14,
                background: 'var(--bg-input)', border: `1.5px solid ${amount > 0 ? cfg.color : 'var(--bd)'}`,
                color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit',
                boxSizing: 'border-box', transition: 'border-color 0.15s',
              }}
            />
            {amount > 0 && (
              <div style={{ fontSize: 12, color: cfg.color, fontWeight: 600, marginTop: 5, paddingLeft: 2 }}>
                = {formatVND(amount)}
              </div>
            )}
          </div>

          {/* ── Date & Note row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, display: 'block' }}>
                Ngày
              </label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13,
                  background: 'var(--bg-input)', border: '1px solid var(--bd)',
                  color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, display: 'block' }}>
                Ghi chú (tuỳ chọn)
              </label>
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Mô tả ngắn…"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13,
                  background: 'var(--bg-input)', border: '1px solid var(--bd)',
                  color: 'var(--tx-1)', outline: 'none', fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* ── Submit ── */}
          <button
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 11,
              fontSize: 14, fontWeight: 700, cursor: isValid ? 'pointer' : 'not-allowed',
              border: 'none',
              background: isValid
                ? `linear-gradient(135deg, ${cfg.color} 0%, ${cfg.color}cc 100%)`
                : 'var(--bg-card-el)',
              color: isValid ? '#fff' : 'var(--tx-5)',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: isValid ? `0 4px 20px ${cfg.color}55` : 'none',
            }}
          >
            <Zap size={15} />
            {submitting ? 'Đang lưu…' : `Ghi ${cfg.label}${amount > 0 ? ' · ' + formatVND(amount) : ''}`}
          </button>

          <p style={{ fontSize: 10, color: 'var(--tx-5)', textAlign: 'center', margin: 0 }}>
            Giao dịch BUY/SELL sẽ không tự liên kết tài sản — vào tab Giao dịch để chỉnh sửa đầy đủ.
          </p>
        </div>
      )}
    </Modal>
  );
}

// ── Floating Action Button ─────────────────────────────────────────────────────

interface FABProps {
  onClick: () => void;
}

export function QuickTransactionFAB({ onClick }: FABProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="Tạo giao dịch nhanh  (Shift+N)"
      style={{
        position: 'fixed',
        bottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
        right: 28,
        zIndex: 200,
        width:  hovered ? 'auto' : 52,
        height: 52,
        borderRadius: 26,
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: hovered ? 8 : 0,
        padding: hovered ? '0 20px' : '0',
        boxShadow: '0 6px 28px rgba(16,185,129,0.45)',
        transition: 'all 0.2s cubic-bezier(0.34,1.56,0.64,1)',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      <Zap size={20} color="#fff" strokeWidth={2.2} />
      {hovered && (
        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.01em' }}>
          Giao dịch nhanh
        </span>
      )}
    </button>
  );
}
